import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Divider,
  CircularProgress,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AppHeader from '../components/AppHeader';
import AlertModal from '../components/AlertModal';
import { updateOrderStatus } from '../services/orderService';
import { useOrders } from '../contexts/OrderContext';

const getStatusColor = (status) => {
  const normalizedStatus = status?.trim().toLowerCase();
  switch (normalizedStatus) {
    case 'confirmed': return '#10B981';
    case 'preparing': return '#F59E0B';
    case 'ready to pickup': return '#851E41';
    case 'out for delivery': return '#8B5CF6';
    case 'rider assigned': return '#06B6D4';
    default: return '#6B7280';
  }
};

const OrderDetailsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { fetchOrders } = useOrders();
  const [order, setOrder] = useState(location.state?.order || null);
  const [loading, setLoading] = useState(false);
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertTitle, setAlertTitle] = useState('');
  const [alertMessage, setAlertMessage] = useState('');

  const showAlert = (title, message) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertVisible(true);
  };

  if (!order) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC' }}>
        <AppHeader title="Order Details" showBack onBack={() => navigate('/orders')} />
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography>No order data available</Typography>
          <Button onClick={() => navigate('/orders')} sx={{ mt: 2 }}>
            Go to Orders
          </Button>
        </Box>
      </Box>
    );
  }

  const statusColor = getStatusColor(order.status);

  const handleStatusChange = async (newStatus) => {
    setLoading(true);
    try {
      await updateOrderStatus(order._id, newStatus);
      setOrder({ ...order, status: newStatus });
      await fetchOrders();
      showAlert('Success', `Order status updated to ${newStatus}`);
    } catch (error) {
      
      showAlert('Error', 'Failed to update order status');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => `₹${parseFloat(amount || 0).toFixed(2)}`;

  const itemsTotal = (order.items || []).reduce(
    (sum, item) => sum + (item.discountedPrice ?? item.price * item.quantity),
    0
  );
  const packagingCharges = order.packagingCharges || 0;
  const transactionCharge = order.transactionCharge || 0;
  const transactionChargeTax = order.transactionChargeTax || 0;
  const finalAmount = itemsTotal + packagingCharges - transactionCharge - transactionChargeTax;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC' }}>
      <AppHeader title="Order Details" showBack onBack={() => navigate('/orders')} />
      <AlertModal
        visible={alertVisible}
        title={alertTitle}
        message={alertMessage}
        onRequestClose={() => setAlertVisible(false)}
      />

      <Box sx={{ p: 2 }}>
        <Card sx={{ mb: 2, borderRadius: 3 }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#1F2937' }}>
                {order.orderId || order._id}
              </Typography>
              <Box
                sx={{
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 2,
                  bgcolor: `${statusColor}15`,
                }}
              >
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: statusColor, textTransform: 'capitalize' }}>
                  {order.status}
                </Typography>
              </Box>
            </Box>

            <Typography sx={{ fontSize: 14, color: '#6B7280' }}>
              {order.createdAt && new Date(order.createdAt).toLocaleString()}
            </Typography>
          </CardContent>
        </Card>

        {order.customer && (
          <Card sx={{ mb: 2, borderRadius: 3 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1F2937', mb: 2 }}>
                Customer Details
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>
                  {order.customer.name || 'Customer'}
                </Typography>
              </Box>
              {order.customer.phoneNumber && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <PhoneIcon sx={{ fontSize: 16, color: '#6B7280' }} />
                  <Typography sx={{ fontSize: 14, color: '#6B7280' }}>
                    {order.customer.phoneNumber}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {order.deliveryAddress && (
          <Card sx={{ mb: 2, borderRadius: 3 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <LocationOnIcon sx={{ fontSize: 18, color: '#851E41' }} />
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1F2937' }}>
                  Delivery Address
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 14, color: '#374151' }}>
                {order.deliveryAddress.address}
              </Typography>
              {order.deliveryAddress.pincode ? (
                <Typography sx={{ fontSize: 13, color: '#6B7280', mt: 0.5 }}>
                  Pincode: {order.deliveryAddress.pincode}
                </Typography>
              ) : null}
            </CardContent>
          </Card>
        )}

        {order.items && order.items.length > 0 && (
          <Card sx={{ mb: 2, borderRadius: 3 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1F2937', mb: 2 }}>
                Order Items
              </Typography>
              {order.items.map((item, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1F2937' }}>
                        {item.name || item.foodItem?.name || 'Item'}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: '#6B7280' }}>
                        {item.quantity} x {formatCurrency(item.price)}
                      </Typography>
                      {item.variant && (
                        <Typography sx={{ fontSize: 12, color: '#6B7280' }}>
                          Variant: {item.variant}
                        </Typography>
                      )}
                      {item.addOns && item.addOns.length > 0 && (
                        <Typography sx={{ fontSize: 12, color: '#6B7280' }}>
                          Add-ons: {item.addOns.map(a => a.name).join(', ')}
                        </Typography>
                      )}
                    </Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1F2937' }}>
                      {formatCurrency(item.price * item.quantity)}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </CardContent>
          </Card>
        )}

        <Card sx={{ mb: 2, borderRadius: 3 }}>
          <CardContent sx={{ p: 2 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1F2937', mb: 2 }}>
              Order Calculation
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ fontSize: 14, color: '#6B7280' }}>Items Total</Typography>
              <Typography sx={{ fontSize: 14, color: '#1F2937' }}>{formatCurrency(itemsTotal)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ fontSize: 14, color: '#6B7280' }}>Packaging Charges</Typography>
              <Typography sx={{ fontSize: 14, color: '#1F2937' }}>{formatCurrency(packagingCharges)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ fontSize: 14, color: '#6B7280' }}>Transaction Charge</Typography>
              <Typography sx={{ fontSize: 14, color: '#EF4444' }}>-{formatCurrency(transactionCharge)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ fontSize: 14, color: '#6B7280' }}>Transaction Tax</Typography>
              <Typography sx={{ fontSize: 14, color: '#EF4444' }}>-{formatCurrency(transactionChargeTax)}</Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1F2937' }}>Final Amount</Typography>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1F2937' }}>
                {formatCurrency(finalAmount)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', bgcolor: '#F0FDF4', borderRadius: 2, p: 1.5, mt: 1 }}>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#059669' }}>Payout Amount</Typography>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#059669' }}>
                {formatCurrency(order.payoutAmount)}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {order.specialInstructions ? (
          <Card sx={{ mb: 2, borderRadius: 3 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1F2937', mb: 1 }}>
                Special Instructions
              </Typography>
              <Typography sx={{ fontSize: 14, color: '#374151' }}>{order.specialInstructions}</Typography>
            </CardContent>
          </Card>
        ) : null}

        <Box sx={{ display: 'flex', gap: 1 }}>
          {order.status === 'Confirmed' && (
            <Button
              fullWidth
              variant="contained"
              startIcon={<RestaurantIcon />}
              onClick={() => handleStatusChange('Preparing')}
              disabled={loading}
              sx={{
                bgcolor: '#F59E0B',
                color: '#fff',
                '&:hover': { bgcolor: '#D97706' },
                py: 1.5,
              }}
            >
              {loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Start Preparing'}
            </Button>
          )}
          {order.status === 'Preparing' && (
            <Button
              fullWidth
              variant="contained"
              startIcon={<CheckCircleIcon />}
              onClick={() => handleStatusChange('Ready to Pickup')}
              disabled={loading}
              sx={{
                bgcolor: '#10B981',
                color: '#fff',
                '&:hover': { bgcolor: '#059669' },
                py: 1.5,
              }}
            >
              {loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Mark as Ready'}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default OrderDetailsPage;